import React from 'react';

/**
 * React 19 Document Metadata Component
 * Provides dynamic document metadata using React 19 features
 */

interface DocumentMetadataProps {
  title?: string;
  description?: string;
  keywords?: string[];
  author?: string;
  ogImage?: string;
  ogType?: string;
  canonicalUrl?: string;
  robots?: string;
}

/**
 * Document Metadata Component for React 19
 * Dynamically updates document head with metadata
 */
export const DocumentMetadata: React.FC<DocumentMetadataProps> = ({
  title,
  description,
  keywords = [],
  author,
  ogImage,
  ogType = 'website',
  canonicalUrl,
  robots = 'index, follow',
}) => {
  // Update document title
  React.useEffect(() => {
    if (title) {
      document.title = title;
    }
  }, [title]);

  // Update meta tags
  React.useEffect(() => {
    const updateMetaTag = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = name;
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    const updatePropertyTag = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    if (description) {
      updateMetaTag('description', description);
      updatePropertyTag('og:description', description);
    }

    if (keywords.length > 0) {
      updateMetaTag('keywords', keywords.join(', '));
    }

    if (author) {
      updateMetaTag('author', author);
    }

    if (ogImage) {
      updatePropertyTag('og:image', ogImage);
    }

    updatePropertyTag('og:type', ogType);

    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = canonicalUrl;
    }

    updateMetaTag('robots', robots);
  }, [description, keywords, author, ogImage, ogType, canonicalUrl, robots]);

  // Return null as this component doesn't render anything
  return null;
};

/**
 * Hook for managing document metadata
 * @returns Functions to update document metadata
 */
export function useDocumentMetadata() {
  const updateTitle = React.useCallback((newTitle: string) => {
    document.title = newTitle;
  }, []);

  const updateDescription = React.useCallback((newDescription: string) => {
    let meta = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    meta.content = newDescription;
  }, []);

  const updateKeywords = React.useCallback((newKeywords: string[]) => {
    let meta = document.querySelector('meta[name="keywords"]') as HTMLMetaElement;
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'keywords';
      document.head.appendChild(meta);
    }
    meta.content = newKeywords.join(', ');
  }, []);

  return {
    updateTitle,
    updateDescription,
    updateKeywords,
  };
}

/**
 * Ship-specific metadata component
 */
export const ShipMetadata: React.FC<{
  shipName?: string;
  shipType?: string;
  shipSize?: number;
  techLevel?: number;
}> = ({ shipName, shipType, shipSize, techLevel }) => {
  const title = shipName 
    ? `${shipName} - Traveller Ship Builder (BETA TEST v0.1.4)`
    : 'Traveller Ship Builder - BETA TEST Version 0.1.4';

  const description = shipName && shipType && shipSize
    ? `${shipName} is a ${shipSize}-ton ${shipType} ship${techLevel ? ` at Tech Level ${techLevel}` : ''}. Built with the Traveller Ship Builder (Beta Test Version).`
    : 'Build and design starships for the Traveller RPG with our comprehensive ship builder tool. Currently in Beta Test Version 0.1.4.';

  const keywords = [
    'Traveller',
    'RPG',
    'starship',
    'ship builder',
    'Mongoose Traveller',
    ...(shipName ? [shipName] : []),
    ...(shipType ? [shipType] : []),
    ...(shipSize ? [`${shipSize} ton`] : []),
  ];

  return (
    <DocumentMetadata
      title={title}
      description={description}
      keywords={keywords}
      author="Traveller Ship Builder"
      ogType="website"
      robots="index, follow"
    />
  );
};
